import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { DataVersions, UserDataResponse } from '@shared/api';
import { ApiError, get, put } from '../lib/api';
import * as storage from '../lib/storage';
import { BoardStore, type BoardState } from './boardStore';
import { useSession } from './session';

/** A 401 means the session expired: go back to the login screen. */
function onAuthError<T>(promise: Promise<T>, expire: () => void): Promise<T> {
  return promise.catch((err) => {
    if (err instanceof ApiError && err.status === 401) expire();
    throw err;
  });
}

const BoardContext = createContext<BoardStore | null>(null);

/** How often an open, visible board checks for changes made on other devices. */
const REFRESH_INTERVAL_MS = 60_000;

export function BoardProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const [store, setStore] = useState<BoardStore | null>(null);
  const { refresh } = useSession();

  useEffect(() => {
    const expire = () => void refresh();
    const s = new BoardStore(
      userId,
      {
        load: () => onAuthError(get<UserDataResponse>('/api/user/data'), expire),
        save: async (type, data) => {
          const res = await onAuthError(put<{ version?: string }>(`/api/user/data/${type}`, { data }), expire);
          return res?.version;
        },
        versions: async () =>
          (await onAuthError(get<{ versions: DataVersions }>('/api/user/data-versions'), expire)).versions,
      },
      storage,
    );
    setStore(s);
    void s.init();
    const onOnline = () => void s.resync().then(() => s.refresh());
    const onVisibility = () => {
      // Hidden (app switch, tab close): send pending changes now.
      if (document.visibilityState === 'hidden') void s.flush();
      // Back on screen: bring in what was changed on other devices meanwhile.
      else void s.refresh();
    };
    // While open on screen, check now and then for changes made on another device.
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) void s.refresh();
    }, REFRESH_INTERVAL_MS);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(poll);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      void s.flush().finally(() => s.dispose());
    };
  }, [userId, refresh]);

  if (!store) return null;
  return <BoardContext.Provider value={store}>{children}</BoardContext.Provider>;
}

export function useBoardStore(): BoardStore {
  const store = useContext(BoardContext);
  if (!store) throw new Error('useBoardStore must be used inside BoardProvider');
  return store;
}

export function useBoard(): BoardState {
  const store = useBoardStore();
  return useSyncExternalStore(store.subscribe, store.getState);
}
